import React, { useMemo, useState, forwardRef, useEffect } from 'react';

import TableContainer from '@hud/web-components/Container/TableContainer';
import { DefaultTable } from '@hud/web-components/Table/Table';
import { Search } from '@hud/web-components/Search/Search';
import { ItemCounter } from '@hud/web-components/ItemCounter/ItemCounter';
import {
  Callees,
  Callers,
  FunctionData,
} from '@hud/portal-dtos/webapp-function.dto';
import Pagination from '@hud/web-components/Pagination/Pagination';
import {
  PaginationState,
  SortingState,
  createColumnHelper,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { HorizontalContainer } from '@hud/web-components/Container/HorizontalContainer';
import metricProgressCellRenderer from '../../../components/metricProgressCellRenderer/metricProgressCellRenderer';
import { DEFAULT_INNER_TABLE_PAGE_SIZE } from '../../../consts';
import renderMetric, {
  formatMetric,
} from '../../../components/metricCellRenderer/metricCellRenderer';
import useAnalytics from '../../../hooks/useAnalytics';
import { useFunctionSliceQueryString } from '../../../hooks/useFunctionFilter';
import HoverPopover from '@hud/web-components/HoverPopover/HoverPopover';
import useEnvironment from '../../../hooks/useEnvironment';
import Skeleton from '@hud/web-components/Skeleton/Skeleton';
import { isUnnamed } from '../../../utils';
import { BreadcrumbItem } from '@hud/web-components/Breadcrumbs/Breadcrumbs';
import Styled from '../../../components/FunctionNameCellRenderer/FunctionNameCellRenderer.styles';
import { TimeFrame } from '@hud/portal-dtos/webapp-common.dto';
import renderRunsMetric from '../../../components/metricCellRenderer/runsCellRenderer';

export type InvocationSourcesTableProps = {
  function?: FunctionData;
  loading: boolean;
  back?: number;
  referralBreadcrumbs?: BreadcrumbItem[] | null;
  timeFrame?: TimeFrame;
} & (
  | {
      type: 'callers';
      entities?: Callers;
    }
  | {
      type: 'callees';
      entities?: Callees;
    }
);

function getBaseName(filePath: string): string {
  // Split by slashes (both forward and backward for cross-platform support)
  const parts = filePath.split(/[/\\]/);
  // Return the last part of the array (the file name)
  return parts[parts.length - 1];
}

const columnHelper = createColumnHelper<(Callers | Callees)[0]>();

const InvocationSourcesTable = forwardRef<
  HTMLDivElement,
  InvocationSourcesTableProps
>(({ entities, type, function: func, loading, referralBreadcrumbs, timeFrame }, ref) => {
  const functionFilters = useFunctionSliceQueryString(); // gets both function and global filters
  const analytics = useAnalytics();
  const [globalFilter, setGlobalFilter] = useState('');
  const updatedFunctionFilters = [globalFilter, functionFilters].filter(Boolean).join('&');
  const [sorting, setSorting] = useState<SortingState>([
    {
      id: 'normalizedRuns.value',
      desc: true,
    },
  ]);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: DEFAULT_INNER_TABLE_PAGE_SIZE,
  });
  const { environment } = useEnvironment();

  const columns = useMemo(() => {
    return [
      columnHelper.accessor('signature', {
        header: 'Src Function',
        cell: (cell) => {
          if (type === 'callers' && cell.row.original.id === 0) {
            if (cell.row.original.callerFunctionType === 'TOP_LEVEL') {
              return (
                <HoverPopover
                  clickable={false}
                  body={`${cell.row.original.filePath}:${cell.row.original.lineNumber}`}
                >
                  {getBaseName(cell.row.original.filePath)}
                  {' <module>'}
                </HoverPopover>
              );
            } else if (cell.row.original.callerFunctionType === 'THIRD_PARTY') {
              return (
                <HoverPopover
                  clickable={false}
                  body={`${cell.row.original.filePath}:${cell.row.original.lineNumber}`}
                >
                  {cell.row.original.name}
                  {'() <3rd-party>'}
                </HoverPopover>
              );
            }
          }
          if (type === 'callers' && !cell.getValue()) return 'root-call';
          if (isUnnamed(cell.getValue())) {
            return (
              <Styled.LinkCell
                to={`/explore/functions/${cell.row.original.id}?${updatedFunctionFilters}`}
                state={{ referralBreadcrumbs: referralBreadcrumbs }}
              >
                <Styled.EntityIcon thirdPartyModule={cell.row.original.thirdPartyModule} />
                <Styled.StyledPopover
                  popoverContent={`${cell.row.original.filePath}:${cell.row.original.lineNumber}`}
                >
                  {cell.getValue()}
                </Styled.StyledPopover>
              </Styled.LinkCell>
            );
          }
          return (
            <Styled.LinkCell
              to={`/explore/functions/${cell.row.original.id}?${functionFilters}`}
              state={{ referralBreadcrumbs: referralBreadcrumbs }}
            >
              <Styled.EntityIcon thirdPartyModule={cell.row.original.thirdPartyModule} />
              <Styled.StyledPopover>{cell.getValue()}</Styled.StyledPopover>
            </Styled.LinkCell>
          );
        },
        minSize: 100,
        maxSize: 500,
        size: 150,
        meta: {
          disableAutoFit: true,
        },
      }),
      columnHelper.accessor('metrics.runs.value', {
        id: 'runs.value',
        header: 'Runs (Runs/Day)',
        cell: renderRunsMetric(timeFrame ?? 'week', func?.firstSeenAt),
        minSize: 150,
        maxSize: 250,
        sortingFn: (a, b) =>
          a.original.metrics.runs.value - b.original.metrics.runs.value,
        meta: {
          fitFormatter: formatMetric('number'),
          rightAlign: true,
        },
      }),
      columnHelper.accessor('metrics.errorRate.value', {
        id: 'errorRate.value',
        header: 'Error Rate',
        cell: renderMetric('percent'),
        minSize: 80,
        maxSize: 200,
        sortingFn: (a, b) =>
          a.original.metrics.errorRate.value -
          b.original.metrics.errorRate.value,
        meta: {
          fitFormatter: formatMetric('percent'),
          rightAlign: true,
        },
      }),
      columnHelper.accessor('metrics.durationAvg.value', {
        id: 'durationAvg.value',
        header: 'Dur Avg',
        cell: renderMetric('duration'),
        size: 130,
        sortingFn: (a, b) =>
          a.original.metrics.durationAvg.value -
          b.original.metrics.durationAvg.value,
        meta: {
          fitFormatter: formatMetric('duration'),
          rightAlign: true,
        },
      }),
      columnHelper.accessor('metrics.durationP90.value', {
        id: 'durationP90.value',
        header: 'Dur P90',
        cell: renderMetric('duration'),
        size: 130,
        sortingFn: (a, b) =>
          a.original.metrics.durationP90.value -
          b.original.metrics.durationP90.value,
        meta: {
          fitFormatter: formatMetric('duration'),
          rightAlign: true,
        },
      }),
      ...(type === 'callers'
        ? [
            columnHelper.accessor('metrics.distribution.value', {
              id: 'distribution.value',
              header: 'Distribution',
              cell: metricProgressCellRenderer,
              meta: {
                disableAutoFit: true,
              },
            }),
          ]
        : [
            columnHelper.accessor('metrics.rate.value', {
              id: 'rate.value',
              header: 'Rate',
              cell: metricProgressCellRenderer,
              meta: {
                disableAutoFit: true,
              },
            }),
          ]),
    ];
  }, [type, functionFilters, timeFrame]);

  const table = useReactTable({
    data: entities ?? [],
    columns: columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onPaginationChange: setPagination,
    onGlobalFilterChange: setGlobalFilter,
    columnResizeMode: 'onChange',
    enableSortingRemoval: false,

    state: {
      pagination,
      sorting,
      globalFilter,
    },
    defaultColumn: {
      minSize: 50,
    },
    onSortingChange: setSorting,
  });

  useEffect(() => {
    if (table.getState().sorting.length === 0 || !func) return;
    analytics.capture(
      type === 'callees'
        ? 'function_callers_sorted'
        : 'function_callees_sorted',
      {
        function_id: func.id,
        function_name: func.name,
        column_name: sorting[0]?.id ?? '',
        column_order: sorting[0]?.desc ? 'desc' : 'asc',
        environment: environment?.name,
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sorting]);

  return (
    <TableContainer.Container
      ref={ref}
      title={type === 'callers' ? 'Callers' : 'Callees'}
      id={`function-${type}`}
    >
      <TableContainer.Header>
        <Search
          inline
          value={globalFilter}
          onChange={(e) => setGlobalFilter(e.target.value)}
        />
        {loading ? (
          <Skeleton width={100} />
        ) : entities ? (
          <ItemCounter
            total={entities.length}
            filtered={table.getPrePaginationRowModel().rows.length}
          />
        ) : null}
      </TableContainer.Header>
      <HorizontalContainer>
        <DefaultTable
          table={table}
          label={type === 'callers' ? 'Callers Table' : 'Callees Table'}
          loading={loading}
          loadingRows={5}
        />
      </HorizontalContainer>
      <TableContainer.Footer>
        <Pagination
          pages={loading ? 0 : table.getPageCount()}
          currentPage={
            loading || table.getPageCount() === 0
              ? 0
              : table.getState().pagination.pageIndex + 1
          }
          onPageChange={(page) => table.setPageIndex(page - 1)}
          loading={loading}
        />
      </TableContainer.Footer>
    </TableContainer.Container>
  );
});

export default InvocationSourcesTable;
